import React from 'react';
import { 
  CreditCard, 
  Calendar, 
  Building2, 
  ShoppingCart, 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Hash,
  FileText,
  Settings
} from 'lucide-react';
import { CompanySubscriptionResource } from '../types';

interface CompanySubscriptionCardProps {
  subscription: CompanySubscriptionResource;
}

const CompanySubscriptionCard: React.FC<CompanySubscriptionCardProps> = ({ subscription }) => {
  const formatDate = (dateString: string): string => {
    // Handle different date formats
    let date: Date;
    if (dateString.includes(' ')) {
      // Format: "2025-08-09 23:59:59"
      date = new Date(dateString.replace(' ', 'T'));
    } else {
      // Format: ISO string
      date = new Date(dateString);
    }
    
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      'active': 'bg-green-100 text-green-800',
      'inactive': 'bg-red-100 text-red-800',
      'expired': 'bg-yellow-100 text-yellow-800',
      'pending': 'bg-blue-100 text-blue-800',
      'suspended': 'bg-gray-100 text-gray-800'
    };
    return statusColors[status.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    const statusIcons: Record<string, React.ReactNode> = {
      'active': <CheckCircle className="h-4 w-4" />,
      'inactive': <XCircle className="h-4 w-4" />,
      'expired': <AlertCircle className="h-4 w-4" />,
      'pending': <Clock className="h-4 w-4" />,
      'suspended': <XCircle className="h-4 w-4" />
    };
    return statusIcons[status.toLowerCase()] || <AlertCircle className="h-4 w-4" />;
  };

  const getSubscriptionLevelColor = (level: number) => {
    if (level >= 3) return 'bg-purple-100 text-purple-800';
    if (level >= 2) return 'bg-blue-100 text-blue-800';
    if (level >= 1) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800'; // Level 0
  };

  const getSubscriptionLevelLabel = (level: number) => {
    if (level >= 3) return 'Premium';
    if (level >= 2) return 'Business';
    if (level >= 1) return 'Basic';
    return 'Trial'; // Level 0
  };

  const isExpired = subscription.validUntil ? new Date(subscription.validUntil) < new Date() : false;
  const isExpiringSoon = subscription.validUntil ? {
    days: Math.ceil((new Date(subscription.validUntil).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
    isSoon: Math.ceil((new Date(subscription.validUntil).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) <= 30
  } : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      {/* Subscription Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center mb-2">
              <CreditCard className="h-5 w-5 text-primary-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                Subscription #{subscription.subscriptionId}
              </h3>
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Hash className="h-4 w-4 mr-1" />
                <span>ID: {subscription.id}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                <span>Created: {formatDate(subscription.createdAt)}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-2 ml-4">
            {/* Status Badge */}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription.status)}`}>
              {getStatusIcon(subscription.status)}
              <span className="ml-1 capitalize">{subscription.status}</span>
            </span>
            
            {/* Subscription Level */}
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSubscriptionLevelColor(subscription.subscriptionLevel)}`}>
              <Settings className="h-3 w-3 mr-1" />
              {getSubscriptionLevelLabel(subscription.subscriptionLevel)}
            </span>
          </div>
        </div>

        {/* Description */}
        {subscription.description && (
          <p className="text-sm text-gray-600 italic">"{subscription.description}"</p>
        )}
      </div>

      {/* Subscription Details */}
      <div className="p-4 space-y-4">
        {/* Validity Information */}
        <div className="bg-gray-50 p-3 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Validity Period</span>
            {isExpired && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <XCircle className="h-3 w-3 mr-1" />
                Expired
              </span>
            )}
            {isExpiringSoon && isExpiringSoon.isSoon && !isExpired && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <AlertCircle className="h-3 w-3 mr-1" />
                Expires Soon
              </span>
            )}
          </div>
          {subscription.validUntil ? (
            <div className="text-sm">
              <span className="text-gray-500">Valid until: </span>
              <span className={`font-medium ${isExpired ? 'text-red-600' : isExpiringSoon?.isSoon ? 'text-yellow-600' : 'text-gray-900'}`}>
                {formatDate(subscription.validUntil)}
              </span>
              {isExpiringSoon && !isExpired && (
                <span className="ml-2 text-xs text-gray-500">
                  ({isExpiringSoon.days} days remaining)
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-500 italic">No expiration date</span>
          )}
        </div>

        {/* Limits Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Branch Limit */}
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-center mb-1">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-lg font-semibold text-blue-900">{subscription.maxBranch}</div>
            <div className="text-xs text-blue-600">Max Branches</div>
          </div>

          {/* Transaction Limits */}
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="flex items-center justify-center mb-1">
              <ShoppingCart className="h-5 w-5 text-green-600" />
            </div>
            <div className="text-sm font-semibold text-green-900">
              {subscription.maxTransactionPerMonth === 0 ? 'Unlimited' : subscription.maxTransactionPerMonth.toLocaleString()}
            </div>
            <div className="text-xs text-green-600">Monthly Limit</div>
            <div className="text-xs text-green-500 mt-1">
              {subscription.maxTransactionPerDay === 0 ? 'Unlimited' : subscription.maxTransactionPerDay.toLocaleString()}/day
            </div>
          </div>
        </div>

        {/* FnB Mode */}
        <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
          <div className="flex items-center">
            <Settings className="h-4 w-4 text-purple-600 mr-2" />
            <span className="text-sm font-medium text-purple-900">F&B Mode</span>
          </div>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            subscription.fnbMode 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {subscription.fnbMode ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {/* Additional Information */}
        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs text-gray-500">
            {subscription.subscriptionInvoiceId && (
              <div className="flex items-center">
                <span className="mr-2">Invoice ID:</span>
                <span className="font-mono bg-gray-100 px-2 py-1 rounded">{subscription.subscriptionInvoiceId}</span>
              </div>
            )}
            <div className="flex items-center">
              <span className="mr-2">Level:</span>
              <span className="font-medium">{subscription.subscriptionLevel}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySubscriptionCard;
